import _ from 'underscore';
import URI from 'urijs';
import { Router } from '@bingads-webui/router';

import { removeBasename } from './paths';

export type NavigateParams = Record<string, unknown>;

export interface NavigateOptions {
  params?: NavigateParams;
  state?: unknown;
  formatSpaPath?: boolean;
}

export type HistoryLocation = string | {
  pathname: string;
  search: string;
  state: unknown;
};

export type NavigationPush = (location: HistoryLocation) => void;

export interface NavigationHistory {
  basename?: string;
  push?: NavigationPush;
}

/**
 * The options for navigate function
 * @typedef {Object} Options
 * @property {Object} params adding query params to the url
 * @property {Object} state adding state to the history object to pass objects during navigation, used for history only
 * @property {boolean} formatSpaPath true by default, to format the url as a spa format or false to keep the same url even if it is absolute
 */

/**
 * Function to navigate using the react's router history object if available and if the path exists in route-configuration, otherwise default server-side navigation
 * @param {String} url the url in a string format
 * @param {Options} options options for adding parameters
 * @param {Object} history the react's router history object
 * @returns {void}
 */
export function navigate(url: string, options: NavigateOptions = {}, history: NavigationHistory = {}) {
  const { params = {}, state } = options;
  const uri = URI(url).setSearch(params);
  if (_.has(history, 'push')) {
    const historyWithPush = history as NavigationHistory & { basename?: string; push: NavigationPush };
    // if the url is absolute and its path starts with the basename or if the url is relative, then it means it's an spa path
    if ((uri.is('absolute') && uri.path().toLowerCase().startsWith(historyWithPush.basename?.toLowerCase())) || uri.is('relative')) {
      const pathname = removeBasename(uri.path(), historyWithPush.basename);
      historyWithPush.push({
        pathname,
        search: uri.search(),
        state,
      });
    } else {
      historyWithPush.push(uri.toString());
    }
  } else {
    Router.navigate(uri.toString());
  }
}
